import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Button,
  Box,
  Menu,
  MenuItem,
  ListItemButton,
  Collapse,
} from '@mui/material';
import { useError } from '../context/ErrorContext';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/logo.gif';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Logout from '@mui/icons-material/Logout';
import CircularProgress from '@mui/material/CircularProgress';
import EventIcon from '@mui/icons-material/Event';
import InventoryIcon from '@mui/icons-material/Inventory';
import HomeIcon from '@mui/icons-material/Home';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { getClaimFromToken } from '../utils/tokenUtils';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';

const Header = () => {
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [assetsOpen, setAssetsOpen] = useState(false); // for mobile drawer
  const [squadOpen, setSquadOpen] = useState(false); // for mobile drawer
  const [anchorElAssets, setAnchorElAssets] = useState(null);
  const [anchorElSquad, setAnchorElSquad] = useState(null);
  const [anchorElEvents, setAnchorElEvents] = useState(null);
  const [anchorElCoach, setAnchorElCoach] = useState(null);

  const openAssets = Boolean(anchorElAssets);
  const openSquad = Boolean(anchorElSquad);
  const openEvents = Boolean(anchorElEvents);
  const openCoach = Boolean(anchorElCoach);
  const [eventsOpen, setEventsOpen] = useState(false);
  const [coachOpen, setCoachOpen] = useState(false);
  const { loading, logout } = useAuth();  // Access logout from AuthContext
  const { setErrorMessage } = useError();
  const { errorMessage } = useError();
  const { token } = useAuth();
  const isAdmin = Boolean(getClaimFromToken('IsAdmin') === 'True') || false;

  const handleNavigate = (path) => {
    const isPublic = ['/login', '/register', '/forgottenpassword', '/resetpassword', '/verify-email', '/welcome', '/help'].includes(path);

    if (!token && !isPublic) {
      // Only redirect to login for protected paths
      navigate('/login', { state: { from: { pathname: path } } });
    } else {
      navigate(path);
    }

    setDrawerOpen(false);
    handleCloseMenus();
  };

  const handleOpenAssetsMenu = (event) => {
    setAnchorElAssets(event.currentTarget);
  };

  const handleOpenSquadMenu = (event) => {
    setAnchorElSquad(event.currentTarget);
  };

  const handleOpenEventsMenu = (event) => {
    setAnchorElEvents(event.currentTarget);
  };

  const handleOpenCoachMenu = (event) => {
    setAnchorElCoach(event.currentTarget);
  };

  const handleCloseMenus = () => {
    setAnchorElAssets(null);
    setAnchorElSquad(null);
    setAnchorElEvents(null);
    setAnchorElCoach(null);
  };

  const handleLogout = () => {
    logout();
    handleCloseMenus();
    navigate('/login');
  };

  return (
    <>
      <AppBar position="static" sx={{ backgroundColor: '#B0C4DE', color: '#fff', marginBottom: 10 }}>

        {errorMessage && (
          <Box sx={{ backgroundColor: 'error.main', color: 'white', p: 1, textAlign: 'center', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{errorMessage}</span>
            <Button color="inherit" size="small" onClick={() => setErrorMessage('')}>
              Dismiss
            </Button>
          </Box>
        )}

        {loading && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.7)', // optional dim background
              zIndex: 2000, // make sure it's above everything else
            }}
          >
            <CircularProgress size={60} thickness={5} />
          </Box>
        )}

        <Toolbar>
          <Box
            component="img"
            src={logo}
            alt="Logo"
            sx={{
              margin: -12,
              height: 63,
              marginRight: 2,
              display: { xs: 'none', md: 'block' },
            }}
          />

          {/* Mobile Menu Icon */}
          <IconButton
            edge="start"
            color="inherit"
            sx={{
              display: { xs: 'flex', md: 'none' }
            }}
            onClick={() => setDrawerOpen(true)}
          >
            <MenuIcon />

            <Typography variant="h6" sx={{ flexGrow: 1, ml: 4 }}>
              The Archer's Diary
            </Typography>
          </IconButton>

          <Typography variant="h6" sx={{ flexGrow: 1 }}>
          </Typography>

          {/* Desktop Menu */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1 }}>
            <Button color="inherit" onClick={() => handleNavigate('/dashboard')} startIcon={<HomeIcon />}>Welcome</Button>
            <Button color="inherit" onClick={() => handleNavigate('/account')} startIcon={<AccountCircle />}>Account</Button>
            <Button color="inherit" onClick={() => handleNavigate('/diary')} startIcon={<MenuBookIcon />}>Diary</Button>
            {/* Dropdown for Assets */}
            <Button color="inherit" onClick={handleOpenAssetsMenu} startIcon={<InventoryIcon />} >
              Assets
            </Button>
            <Menu anchorEl={anchorElAssets} open={openAssets} onClose={handleCloseMenus}>
              <MenuItem onClick={() => handleNavigate('/arrows')}>Arrows</MenuItem>
              <MenuItem onClick={() => handleNavigate('/bows')}>Bows</MenuItem>
              <MenuItem onClick={() => handleNavigate('/spine-calculator')}>Spine Calculator</MenuItem>
              <MenuItem onClick={() => handleNavigate('/personal-rounds')}>Target Rounds</MenuItem>
            </Menu>

            <Button color="inherit" onClick={handleOpenSquadMenu} startIcon={<PeopleAltIcon />} >
              My Squad
            </Button>
            <Menu anchorEl={anchorElSquad} open={openSquad} onClose={handleCloseMenus}>
              <MenuItem onClick={() => handleNavigate('/squad-about')}>About My Squad</MenuItem>
              <MenuItem onClick={() => handleNavigate('/member-shares')}>My Squad Invites</MenuItem>
              <MenuItem onClick={() => handleNavigate('/squad-scores')}>My Squad Scores</MenuItem>
            </Menu>

            <Button color="inherit" onClick={handleOpenEventsMenu} startIcon={<EventIcon />} >
              Events
            </Button>
            <Menu anchorEl={anchorElEvents} open={openEvents} onClose={handleCloseMenus}>
              <MenuItem onClick={() => handleNavigate('/competitions')}>Competitions</MenuItem>
              <MenuItem onClick={() => handleNavigate('/practice')}>Practice</MenuItem>
              <MenuItem onClick={() => handleNavigate('/tuning')}>Tuning Sessions</MenuItem>
              <Divider orientation="horizontal" flexItem sx={{ mx: 1, borderColor: '#ddd' }} />
              <MenuItem onClick={() => handleNavigate('/bests')}>Bests</MenuItem>
              <MenuItem onClick={() => handleNavigate('/distance-bests')}>Distance Bests</MenuItem>
              <MenuItem onClick={() => handleNavigate('/handicaps')}>Handicaps</MenuItem>
              <Divider orientation="horizontal" flexItem sx={{ mx: 1, borderColor: '#ddd' }} />
              <MenuItem onClick={() => handleNavigate('/what-score')}>What Score</MenuItem>
            </Menu>

            {isAdmin && (
              <>
                {/* Dropdown for Coach */}
                <Button color="inherit" onClick={handleOpenCoachMenu} startIcon={<ManageAccountsIcon />} >
                  Admin
                </Button>
                <Menu anchorEl={anchorElCoach} open={openCoach} onClose={handleCloseMenus}>
                  <MenuItem onClick={() => handleNavigate('/members')}>Members</MenuItem>
                  <MenuItem onClick={() => handleNavigate('/broadcast')}>Broadcast</MenuItem>
                </Menu>
              </>
            )}

            <Button color="inherit" onClick={() => handleNavigate('/help')} startIcon={<HomeIcon />}>Help</Button>
            <Button color="inherit" onClick={() => handleLogout()} startIcon={<Logout />}>Logout</Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <List sx={{ width: 250 }}>
          <ListItem button onClick={() => handleNavigate('/dashboard')}>
            <ListItemText primary="Welcome" />
          </ListItem>
          <ListItem button onClick={() => handleNavigate('/account')}>
            <ListItemText primary="Account" />
          </ListItem>
          <ListItem button onClick={() => handleNavigate('/diary')}>
            <ListItemText primary="Diary" />
          </ListItem>
          <ListItemButton onClick={() => setAssetsOpen(!assetsOpen)}>
            <ListItemText primary="Assets" />
            {assetsOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={assetsOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <ListItem button sx={{ pl: 4 }} onClick={() => handleNavigate('/arrows')}>
                <ListItemText primary="Arrows" />
              </ListItem>
              <ListItem button sx={{ pl: 4 }} onClick={() => handleNavigate('/bows')}>
                <ListItemText primary="Bows" />
              </ListItem>
              <ListItem button sx={{ pl: 4 }} onClick={() => handleNavigate('/spine-calculator')}>
                <ListItemText primary="Spine Calculator" />
              </ListItem>
              <ListItem button sx={{ pl: 4 }} onClick={() => handleNavigate('/personal-rounds')}>
                <ListItemText primary="Target Rounds" />
              </ListItem>
            </List>
          </Collapse>

          <ListItemButton onClick={() => setSquadOpen(!squadOpen)}>
            <ListItemText primary="My Squad" />
            {squadOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={squadOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <ListItem button sx={{ pl: 4 }} onClick={() => handleNavigate('/squad-about')}>
                <ListItemText primary="About My Squad" />
              </ListItem>
              <ListItem button sx={{ pl: 4 }} onClick={() => handleNavigate('/member-shares')}>
                <ListItemText primary="My Squad Invites" />
              </ListItem>
              <ListItem button sx={{ pl: 4 }} onClick={() => handleNavigate('/squad-scores')}>
                <ListItemText primary="My Squad Scores" />
              </ListItem>
            </List>
          </Collapse>

          {/* Events menu */}
          <ListItemButton onClick={() => setEventsOpen(!eventsOpen)}>
            <ListItemText primary="Events" />
            {eventsOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={eventsOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <ListItem button sx={{ pl: 4 }} onClick={() => handleNavigate('/competitions')}>
                <ListItemText primary="Competition" />
              </ListItem>
              <ListItem button sx={{ pl: 4 }} onClick={() => handleNavigate('/practice')}>
                <ListItemText primary="Practice" />
              </ListItem>
              <ListItem button sx={{ pl: 4 }} onClick={() => handleNavigate('/tuning')}>
                <ListItemText primary="Tuning Sessions" />
              </ListItem>
              <ListItem button sx={{ pl: 4 }} onClick={() => handleNavigate('/bests')}>
                <ListItemText primary="Bests" />
              </ListItem>
              <ListItem button sx={{ pl: 4 }} onClick={() => handleNavigate('/distance-bests')}>
                <ListItemText primary="Distance Bests" />
              </ListItem>
              <ListItem button sx={{ pl: 4 }} onClick={() => handleNavigate('/handicaps')}>
                <ListItemText primary="Handicaps" />
              </ListItem>
              <ListItem button sx={{ pl: 4 }} onClick={() => handleNavigate('/what-score')}>
                <ListItemText primary="What Score" />
              </ListItem>
            </List>
          </Collapse>

          {isAdmin && (
            <>
              <ListItemButton onClick={() => setCoachOpen(!coachOpen)}>
                <ListItemText primary="Admin" />
                {eventsOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>

              <Collapse in={coachOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <ListItem button sx={{ pl: 4 }} onClick={() => handleNavigate('/members')}>
                    <ListItemText primary="Members" />
                  </ListItem>
                  <ListItem button sx={{ pl: 4 }} onClick={() => handleNavigate('/broadcast')}>
                    <ListItemText primary="Broadcast" />
                  </ListItem>
                </List>
              </Collapse>
            </>
          )}

          <ListItem button onClick={() => handleNavigate('/help')}>
            <ListItemText primary="Help" />
          </ListItem>

          <ListItem button onClick={() => handleLogout()}>
            <ListItemText primary="Logout" />
          </ListItem>
        </List>
      </Drawer>
    </>
  );
};

export default Header;
